/**
 * Authentication Client
 * Handles login, logout, and session management
 */

import type { AuthClientConfig } from "./types";
import { logger } from "@/lib/utils/logger";
import posthog from "posthog-js";

export class AuthClient {
  private config: AuthClientConfig;
  private sessionId: string | null = null;

  constructor(config: AuthClientConfig) {
    this.config = config;
  }

  /**
   * Get authentication headers (including CSRF token)
   * Now async to support CSRF token retrieval from cookies/API
   */
  async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // If API keys are present, we're in token mode — no CSRF needed
    // (FrappeClient will add Authorization header separately)
    if (this.config.apiKey && this.config.apiSecret) {
      return headers;
    }

    // Only bother with CSRF if we're actually using session cookies
    if (this.config.useSession) {
      const csrfToken = await this.getCsrfToken();
      if (csrfToken) {
        headers["X-Frappe-CSRF-Token"] = csrfToken;
      }
    }

    // PostHog session ID for Sentry ↔ PostHog session linking on backend
    try {
      const sessionId = posthog.get_session_id?.();
      if (sessionId) {
        headers["X-PostHog-Session-Id"] = sessionId;
      }
    } catch {
      // PostHog not initialized — skip silently
    }

    return headers;
  }

  /**
   * Get authentication headers for form-encoded requests
   * Useful for endpoints that need application/x-www-form-urlencoded
   */
  async getFormAuthHeaders(): Promise<Record<string, string>> {
    const headers = await this.getAuthHeaders();
    // Override Content-Type for form-encoded requests
    headers["Content-Type"] = "application/x-www-form-urlencoded";
    return headers;
  }

  /**
   * Get CSRF token from cookies or API
   * Reads from cookies first (fastest), falls back to API if needed
   * Returns null on failure (non-throwing)
   */
  private async getCsrfToken(): Promise<string | null> {
    // Early returns: don't fetch CSRF if not using session or if API keys exist
    if (!this.config.useSession) {
      return null;
    }
    if (this.config.apiKey && this.config.apiSecret) {
      return null;
    }

    // Try cookies first (fastest path)
    if (typeof document !== "undefined") {
      const cookies = document.cookie.split(";");
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split("=");
        // Frappe uses various cookie names for CSRF token
        if (name === "csrf_token" || name === "csrf-token" || name === "csrf_token_http") {
          return decodeURIComponent(value);
        }
      }
    }

    // Fallback: fetch from whitelisted API endpoint
    try {
      const baseUrl = this.config.url.replace(/\/$/, "");
      const apiPrefix = baseUrl === "/api" ? "" : "/api";
      const response = await fetch(
        `${baseUrl}${apiPrefix}/method/zaviago_backend.api.session.get_csrf_token`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      // Support both formats:
      // { csrf_token: "..." }  (our custom method)
      // { message: { csrf_token: "..." } }  (Frappe wrapper)
      const token = data.csrf_token ?? data.message?.csrf_token ?? null;
      
      return token;
    } catch (error) {
      // Non-throwing: just return null
      return null;
    }
  }

  /**
   * Get session ID (if available)
   */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Set session ID
   */
  setSessionId(sessionId: string | null): void {
    this.sessionId = sessionId;
  }

  /**
   * Login to get session (if using session-based auth)
   */
  async login(username: string, password: string): Promise<string> {
    if (!this.config.useSession) {
      throw new Error("Session-based authentication is not enabled");
    }

    // Frappe login endpoint - try /api/method/login first (more reliable)
    const baseUrl = this.config.url.replace(/\/$/, "");
    // If baseUrl is "/api" (Vite proxy), don't add another "/api" prefix
    // If baseUrl is absolute (http://...), add "/api" prefix
    const apiPrefix = baseUrl === "/api" ? "" : "/api";
    const loginUrl = `${baseUrl}${apiPrefix}/method/login`;

    // Use URLSearchParams to properly encode the password (handles special chars like #)
    const formData = new URLSearchParams();
    formData.append("usr", username);
    formData.append("pwd", password);

    const response = await fetch(loginUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "Accept": "application/json",
      },
      credentials: "include",
      body: formData.toString(),
    });

    if (!response.ok) {
      let errorMessage = "Login failed";
      try {
        const error = await response.json();
        errorMessage = error.message || error.exc || error.exc_type || errorMessage;
        logger.error("Frappe login error:", error);
      } catch (e) {
        const text = await response.text().catch(() => "");
        errorMessage = `Login failed with status ${response.status}`;
        logger.error("Frappe login error (non-JSON):", text);
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();

    if (result.message !== "Logged In" && result.message !== "No App") {
      throw new Error(result.message || "Login failed");
    }

    // Try to get the logged-in user, but don't fail if it doesn't work
    // The session cookie should be set, so we can proceed
    let loggedInUser = username; // Default to the username we used

    try {
      // If baseUrl is "/api" (Vite proxy), don't add another "/api" prefix
      // If baseUrl is absolute (http://...), add "/api" prefix
      const verifyApiPrefix = baseUrl === "/api" ? "" : "/api";
      const verifyUrl = `${baseUrl}${verifyApiPrefix}/method/frappe.auth.get_logged_user`;
      const verifyResponse = await fetch(verifyUrl, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (verifyResponse.ok) {
        const userData = await verifyResponse.json();
        if (userData.message && userData.message !== "Guest") {
          loggedInUser = userData.message;
        }
      }
    } catch (error) {
      // Verification failed, but login succeeded, so we'll use the username
      logger.warn("Could not verify logged-in user, using provided username:", error);
    }

    // Extract session ID from cookies for reference
    const cookies = response.headers.get("set-cookie");
    if (cookies) {
      const sidMatch = cookies.match(/sid=([^;]+)/);
      if (sidMatch) {
        this.sessionId = sidMatch[1];
      }
    }

    // Diagnostic: Check if sid cookie was actually set (for CORS debugging)
    if (import.meta.env.DEV && this.config.useSession) {
      const hasSid = typeof document !== "undefined" && 
                     document.cookie.split(";").some(c => c.trim().startsWith("sid="));
      if (!hasSid) {
        logger.warn(
          "[AuthClient] Login succeeded but no 'sid' cookie found. " +
          "Check CORS/credentials and cookie settings on the Frappe site."
        );
      }
    }

    return loggedInUser;
  }

  /**
   * Logout (clear session)
   */
  async logout(): Promise<void> {
    if (this.config.useSession) {
      const baseUrl = this.config.url.replace(/\/$/, "");
      // If baseUrl is "/api" (Vite proxy), don't add another "/api" prefix
      // If baseUrl is absolute (http://...), add "/api" prefix
      const apiPrefix = baseUrl === "/api" ? "" : "/api";
      const logoutUrl = `${baseUrl}${apiPrefix}/method/logout`;
      const headers = await this.getAuthHeaders();
      await fetch(logoutUrl, {
        method: "POST",
        headers,
        credentials: "include",
      });
      this.sessionId = null;
    }
  }
}

